import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Gift, RotateCcw } from 'lucide-react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Pagination,
  Select,
  Skeleton,
  type Column,
} from '../../components/ui';
import { api, compact } from '../../lib/api';
import { TYPING_DEBOUNCE_MS, useDebounced } from '../../lib/debounce';
import { formatDateTime } from '../../lib/format';
import { useT } from '../../lib/i18n';
import { errorMessage, useToast } from '../../lib/toast';
import type {
  Paginated,
  ReferralReward,
  ReferralRewardStatus,
} from '../../lib/types';
import { CampaignSelect, formatReward, REWARD_TONES, UserRef } from './shared';

/**
 * What the program has paid, and what it failed to. A failed reward keeps its
 * reason and can be tried again; a paid one can never be paid twice, so retry
 * is only offered where it is safe.
 */
export function RewardsTab() {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [retrying, setRetrying] = useState<ReferralReward | null>(null);

  const filters = {
    campaignId: params.get('campaignId') ?? '',
    status: params.get('status') ?? '',
    deviceId: params.get('deviceId') ?? '',
  };
  // Typed a character at a time; one request per pause, not per keystroke.
  const deviceId = useDebounced(filters.deviceId, TYPING_DEBOUNCE_MS);

  const set = (key: keyof typeof filters, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  };

  const query = useMemo(
    () =>
      compact({
        campaignId: filters.campaignId,
        status: filters.status,
        deviceId,
        page,
        limit: 25,
      }),
    [filters.campaignId, filters.status, deviceId, page],
  );

  const rewards = useQuery({
    queryKey: ['referrals', 'rewards', query],
    queryFn: () => api.get<Paginated<ReferralReward>>('/api/referrals/rewards', query),
  });

  const retry = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<ReferralReward>(`/api/referrals/rewards/${id}/retry`, { reason }),
    onSuccess: (reward) => {
      if (reward.status === 'granted') toast.success(t('referrals.rewards.retried'));
      else toast.error(t('referrals.rewards.retryFailed', { reason: reward.failureReason ?? '' }));
      setRetrying(null);
      void queryClient.invalidateQueries({ queryKey: ['referrals'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const columns: Column<ReferralReward>[] = [
    {
      key: 'referrer',
      header: t('referrals.rewards.referrer'),
      render: (row) => <UserRef id={row.userId} />,
    },
    {
      key: 'reward',
      header: t('referrals.rewards.reward'),
      render: (row) => (
        <span className="font-medium">{formatReward(row.rewardType, row.amount)}</span>
      ),
    },
    {
      key: 'campaign',
      header: t('referrals.rewards.campaign'),
      render: (row) => (
        <span className="break-words">{row.campaignName ?? t('referrals.archived')}</span>
      ),
    },
    {
      key: 'ordinal',
      header: t('referrals.rewards.ordinal'),
      numeric: true,
      desktopOnly: true,
      render: (row) => `#${row.ordinal}`,
    },
    {
      key: 'status',
      header: t('referrals.rewards.status'),
      render: (row) => (
        <span className="inline-flex flex-col items-end gap-0.5">
          <Badge tone={REWARD_TONES[row.status]}>
            {t(`referrals.rewardStatus.${row.status}`)}
          </Badge>
          {row.failureReason && (
            <span className="max-w-[220px] text-right text-[11px] break-words text-danger">
              {row.failureReason}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'when',
      header: t('referrals.rewards.when'),
      render: (row) => formatDateTime(row.grantedAt ?? row.createdAt),
    },
    {
      key: 'actions',
      header: '',
      render: (row) =>
        row.status === 'failed' ? (
          <Button
            className="text-xs"
            icon={<RotateCcw size={13} />}
            onClick={() => setRetrying(row)}
          >
            {t('referrals.rewards.retry')}
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-3 rounded-xl border border-line bg-surface p-3.5 sm:grid-cols-3 sm:p-4">
        <CampaignSelect value={filters.campaignId} onChange={(value) => set('campaignId', value)} />
        <Field label={t('referrals.filter.status')}>
          <Select value={filters.status} onChange={(event) => set('status', event.target.value)}>
            <option value="">{t('referrals.filter.anyStatus')}</option>
            {(['granted', 'failed', 'pending'] as ReferralRewardStatus[]).map((value) => (
              <option key={value} value={value}>
                {t(`referrals.rewardStatus.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('referrals.filter.referrer')}>
          <Input
            className="font-mono text-xs"
            value={filters.deviceId}
            onChange={(event) => set('deviceId', event.target.value.trim())}
            placeholder={t('common.pasteDeviceId')}
          />
        </Field>
      </div>

      {rewards.isError && <ErrorNote error={rewards.error} />}

      <Card>
        {rewards.isPending ? (
          <Skeleton className="h-96" />
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={rewards.data?.items ?? []}
              rowKey={(row) => row.id}
              empty={
                <EmptyState
                  icon={<Gift size={28} />}
                  title={t('referrals.rewards.emptyTitle')}
                  description={t('referrals.rewards.emptyDescription')}
                />
              }
            />
            {(rewards.data?.items.length ?? 0) > 0 && (
              <Pagination
                page={rewards.data?.page ?? 1}
                totalPages={rewards.data?.totalPages ?? 1}
                total={rewards.data?.total ?? 0}
                onChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <ConfirmDialog
        open={retrying !== null}
        title={t('referrals.rewards.confirmTitle')}
        description={
          retrying
            ? t('referrals.rewards.confirmDescription', {
                reward: formatReward(retrying.rewardType, retrying.amount),
              })
            : undefined
        }
        confirmLabel={t('referrals.rewards.retry')}
        loading={retry.isPending}
        onCancel={() => setRetrying(null)}
        onConfirm={(reason) => retrying && retry.mutate({ id: retrying.id, reason })}
      />
    </div>
  );
}
